'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import type { ProductItem } from '@lib/models/types'
import type { CompatibilityMatch } from '@lib/products'
import { captureProductStripScrolled } from '@lib/interaction-telemetry'
import { ProductCard } from '@components/cards/ProductCard/ProductCard'
import './categorySection.css'

export type CategorySectionProps = {
  /** Category enum value — doubles as the anchor id the chips scroll to. */
  category: string
  label: string
  products: ProductItem[]
  flaggedIds: Set<string>
  compatibilityMap: Map<string, number> | null
  compatibilityDetailsMap?: Map<string, CompatibilityMatch> | null
  onFlagSuccessAction: (id: string) => void
  sectionRefAction?: (el: HTMLElement | null) => void
}

export function CategorySection({
  category,
  label,
  products,
  flaggedIds,
  compatibilityMap,
  compatibilityDetailsMap,
  onFlagSuccessAction,
  sectionRefAction,
}: CategorySectionProps) {
  const t = useTranslations('productsPage')
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollFades = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 8)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8)
  }, [])

  const visible = products.filter((p) => !flaggedIds.has(p.id))

  useEffect(() => {
    updateScrollFades()
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', updateScrollFades, { passive: true })
    const ro = new ResizeObserver(updateScrollFades)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollFades)
      ro.disconnect()
    }
  }, [updateScrollFades, visible.length])

  const scrollBy = (direction: 'left' | 'right') => {
    captureProductStripScrolled(category, direction)
    const el = scrollRef.current
    if (!el) return
    const cards = Array.from(el.querySelectorAll<HTMLElement>('.category-section-card'))
    if (cards.length === 0) return

    // Pick the card nearest the scrollport center, then advance one so the arrow
    // lands the next/prev product in the same centered spot (esp. on mobile).
    const viewportCenter = el.scrollLeft + el.clientWidth / 2
    let nearestIndex = 0
    let nearestDist = Infinity
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i]
      const cardCenter = card.offsetLeft + card.offsetWidth / 2
      const dist = Math.abs(cardCenter - viewportCenter)
      if (dist < nearestDist) {
        nearestDist = dist
        nearestIndex = i
      }
    }

    const target = cards[direction === 'right' ? nearestIndex + 1 : nearestIndex - 1]
    if (!target) return

    const targetCenter = target.offsetLeft + target.offsetWidth / 2
    el.scrollTo({
      left: targetCenter - el.clientWidth / 2,
      behavior: 'smooth',
    })
  }

  if (visible.length === 0) return null

  const headingId = `category-heading-${category}`

  return (
    <section
      id={category}
      ref={sectionRefAction}
      data-category={category}
      aria-labelledby={headingId}
      className="category-section"
    >
      <h2 id={headingId} className="sr-only">
        {label}
      </h2>
      <div id={`category-watermark-${category}`} className="category-section-watermark" aria-hidden="true">
        <span id={`category-watermark-label-${category}`}>{label}</span>
      </div>

      <div id={`category-scroll-wrap-${category}`} className="category-section-scroll-wrap">
        {canScrollLeft && (
          <div id={`category-fade-left-${category}`} className="category-section-fade-left">
            <button
              id={`category-scroll-left-${category}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => scrollBy('left')}
              aria-label={t('scrollLeft')}
              className="category-section-scroll-btn category-section-scroll-btn--left"
            >
              <svg
                id={`category-scroll-left-icon-${category}`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}

        <div
          id={`category-scroll-${category}`}
          ref={scrollRef}
          className="category-section-scroll"
          data-testid={`category-scroll-${category}`}
        >
          {visible.map((product) => (
            <div
              key={product.id}
              id={`category-card-slot-${product.id}`}
              className="category-section-card"
            >
              <ProductCard
                product={product}
                compatibilityScore={compatibilityMap?.get(product.id)}
                compatibilityDetail={compatibilityDetailsMap?.get(product.id)}
                onFlagSuccessAction={() => onFlagSuccessAction(product.id)}
              />
            </div>
          ))}
        </div>

        {canScrollRight && (
          <div id={`category-fade-right-${category}`} className="category-section-fade-right">
            <button
              id={`category-scroll-right-${category}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => scrollBy('right')}
              aria-label={t('scrollRight')}
              className="category-section-scroll-btn category-section-scroll-btn--right"
            >
              <svg
                id={`category-scroll-right-icon-${category}`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M6 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
